import { fetchCourseProduct } from "@/api/store"
import { fetchComments, postComment, postCommentVote } from "../../api/comments"
import {
  getCourses,
  getActiveCourses,
  getActiveCourseProgress,
  trackActiveCourse,
  untrackActiveCourse,
  getCourseByShortcode,
  getChaptersByCourseId,
  getLessonById,
  logLessonCompletion,
  logLessonAttempt,
  submitLessonRating,
} from "../../api/course"

const state = {
  courses: [],
  totalCount: 0,
  activeCourses: [],
  activeCoursesProgress: null,
  currentCourse: null,
  lessons: [],
  currentLesson: null,
  currentCoursesPage: 0,
  totalCoursesPages: 0,
  lessonComments: [],
  completedLessons: {},
  unlockModalShowing: false,
  lessonNotification: null,
  showTrailer: false,
  courseProduct: null,
}

const getters = {
  courses: state => state.courses,
  totalCount: state => state.totalCount,
  activeCourses: state => state.activeCourses,
  activeCoursesProgress: state => state.activeCoursesProgress,
  currentCourse: state => state.currentCourse,
  lessons: state => state.lessons,
  lessonsById: state => {
    const lessonsById = {}
    for (let lesson of state.lessons) {
      lessonsById[lesson.id] = lesson
    }
    return lessonsById
  },
  currentLesson: state => state.currentLesson,
  currentCoursesPage: state => state.currentCoursesPage,
  totalCoursesPages: state => state.totalCoursesPages,
  lessonComments: state => state.lessonComments,
  completedLessons: state => state.completedLessons,
  unlockModalShowing: state => state.unlockModalShowing,
  showTrailer: state => state.showTrailer,
  courseProduct: state => state.courseProduct,
}

const mutations = {
  setCourses: (state, courses) => (state.courses = courses),
  setTotalCount: (state, count) => (state.totalCount = count),
  setActiveCourses: (state, activeCourses) =>
    (state.activeCourses = activeCourses),
  setActiveCoursesProgress: (state, activeCoursesProgress) =>
    (state.activeCoursesProgress = activeCoursesProgress),
  setCurrentCourse: (state, course) => (state.currentCourse = course),
  setLessons: (state, lessons) => (state.lessons = lessons),
  setCurrentLesson: (state, lesson) => (state.currentLesson = lesson),
  setCurrentCoursesPage: (state, page) => (state.currentCoursesPage = page),
  setTotalCoursesPages: (state, number) => (state.totalCoursesPages = number),
  setLessonComments: (state, comments) => (state.lessonComments = comments),
  setCompletedLessons: (state, lessons) => (state.completedLessons = lessons),
  setUnlockModalShowing: (state, unlockModalShowing) =>
    (state.unlockModalShowing = unlockModalShowing),
  setShowTrailer: (state, showTrailer) => (state.showTrailer = showTrailer),
  setCourseProduct: (state, courseProduct) =>
    (state.courseProduct = courseProduct),
}

const actions = {
  async fetchCourses ({ state, commit }, { game, pageNumber }) {
    if (
      state.courses &&
      state.courses.length > 0 &&
      state.courses[0].game === game
    ) {
      return
    }
    const payload = await getCourses({ game, pageNumber })
    commit("setCourses", payload.results)
    commit("setTotalCount", payload.total_count)
    commit("setCurrentCoursesPage", payload.page)
    commit("setTotalCoursesPages", payload.num_pages - 1)
  },
  async fetchMoreCourses ({ state, commit }, { game }) {
    if (state.currentCoursesPage >= state.totalCoursesPages) {
      return
    }
    const courses = state.courses
    const data = await getCourses({
      game,
      pageNumber: state.currentCoursesPage + 1,
    })
    commit("setCourses", [...courses, ...data.results])
    commit("setCurrentCoursesPage", data.page)
    commit("setTotalCoursesPages", data.num_pages - 1)
  },
  async fetchActiveCourses ({ commit }) {
    const courses = await getActiveCourses()
    await commit("setActiveCourses", courses)
  },
  async fetchActiveCoursesProgress ({ commit }) {
    const activeCoursesProgress = await getActiveCourseProgress()
    await commit("setActiveCoursesProgress", activeCoursesProgress)
  },
  async untrackActiveCourse (context, courseId) {
    await untrackActiveCourse(courseId)
  },
  async getCourseByShortcode ({ commit }, shortcode) {
    const data = await getCourseByShortcode(shortcode)
    commit("setCurrentCourse", data)
  },
  async getChaptersByCourseId ({ commit }, courseId) {
    const data = await getChaptersByCourseId(courseId)
    let lessons = []
    for (let chapter of data) {
      const segments = chapter.segments.map(segment => {
        segment.chapter = chapter.id
        return segment
      })
      lessons = [...lessons, ...segments]
    }
    commit("setLessons", lessons)
  },
  async getLessonById ({ commit }, { course, chapter, lesson }) {
    const data = await getLessonById(course, chapter, lesson)
    commit("setCurrentLesson", data)
  },
  async trackActiveCourse ({ state }) {
    if (state.currentCourse) {
      trackActiveCourse(state.currentCourse.id)
    }
  },
  async fetchLessonComments ({ commit, state }) {
    const data = await fetchComments("pgc_segment", state.currentLesson.id)
    commit("setLessonComments", data.comments)
  },
  async logLessonCompletion ({ state, commit }) {
    const completedLessons = state.completedLessons
    completedLessons[state.currentLesson.id] = 1
    commit("setCompletedLessons", { ...completedLessons })

    await logLessonCompletion(state.currentLesson.id)
  },
  async logLessonAttempt ({ state }) {
    await logLessonAttempt(state.currentLesson.id)
  },
  async voteComment ({ dispatch }, commentId) {
    await postCommentVote(commentId, "upvote")
    dispatch("fetchLessonComments")
  },
  async postNewComment ({ dispatch, state }, comment) {
    const body = {
      text: comment.text,
      language: "en_US",
      parent_comment_id: comment.parent_comment_id,
    }
    await postComment("pgc_segment", state.currentLesson.id, body)
    dispatch("fetchLessonComments")
  },
  async submitLessonRating ({ commit }, { course, chapter, lesson, rating }) {
    const status = await submitLessonRating(rating, course, chapter, lesson)
    if (status === 204) {
      return rating
    }
    return false
  },
  async fetchCourseProduct ({ commit }, shortcode) {
    const data = await fetchCourseProduct(shortcode)
    commit("setCourseProduct", data ? data.product : null)
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}



// WEBPACK FOOTER //
// ./src/store/modules/courses.js